// SHER-636 — decide how a submission save/update error should surface in the
// patient workspace.
//
// A failed save/update must NOT collapse the whole patient view (mirror the
// SHER-537 delete fix): it shows an inline snackbar and keeps the record and
// workspace mounted. The reported bug was a cross-author 403 whose *string*
// errorCode ("NOT_ORIGINAL_AUTHOR") fell through the numeric-only handling in
// patient/index.jsx and hit setError(true), blanking the page. Page-level
// collapse stays reserved for load failures (no save/fill action in flight).

export const SUBMISSION_ERROR_OFFLINE = 2;
export const SUBMISSION_ERROR_PERMISSION = 401;

/**
 * @param {number|string|boolean} error  patientsSubmissions.error from redux
 * @param {string|undefined} action      the current flow: "update" | "fill" | undefined
 * @returns {{collapse: boolean, severity: 'error'|'warning', message: string|null, silent?: boolean}}
 *   `silent` marks a refusal another surface is already handling (a modal asking for
 *   the missing input): the caller must not raise a snackbar for it.
 */
export function resolveSubmissionUpdateError(error, action) {
    const isSaveContext = action === 'update' || action === 'fill';

    // Offline: the record was queued locally — a warning, never a collapse.
    if (error === SUBMISSION_ERROR_OFFLINE) {
        return {
            collapse: false,
            severity: 'warning',
            message: action === 'fill'
                ? 'hospital.patient.new-record-offline'
                : 'hospital.patient.updated-record-offline',
        };
    }

    // SHER-786: refusals the clinician can resolve without leaving the screen are not
    // announced at all. The API answers these when it needs one more input, and the
    // component that asked for the save opens the modal that collects it — a reason,
    // or a password. Firing the generic error snackbar alongside that modal tells the
    // clinician the save failed while the app is still asking them to finish it, which
    // is exactly what live QA saw: the reason modal and an error toast, together.
    // Anything NOT recoverable in place keeps its message; silence here is reserved
    // for the cases where another surface is already handling the outcome.
    if (error === 'EDIT_REASON_REQUIRED' || error === 'PASSWORD_REQUIRED' || error === 'INVALID_PASSWORD') {
        return { collapse: false, silent: true, severity: 'warning', message: null };
    }

    // Authorization failures: clear, specific, non-fatal messages.
    if (error === SUBMISSION_ERROR_PERMISSION) {
        return { collapse: false, severity: 'error', message: 'hospital.patient.error.permission' };
    }
    if (error === 'NOT_ORIGINAL_AUTHOR') {
        return { collapse: false, severity: 'error', message: 'hospital.patient.error.not-original-author' };
    }
    if (error === 'PRESCRIPTION_AUTHOR_LOCKED') {
        return { collapse: false, severity: 'error', message: 'hospital.patient.error.prescription-author-locked' };
    }

    // Any other error: in a save/update context it stays non-fatal (SHER-636 /
    // SHER-537) with the general message; outside a save context (e.g. a load
    // failure) it remains a page-level collapse.
    return {
        collapse: !isSaveContext,
        severity: 'error',
        message: 'hospital.patient.error.general',
    };
}
